
import { useCallback, useRef, useState } from "react";
import { ApiError } from "@/lib/api/client";
import {
  createWorkbookExport,
  downloadExportJobBlob,
  getExportJob,
} from "@/lib/api/endpoints";

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Trigger a real file download. A programmatic `<a>` click honours the route's
 * `Content-Disposition: attachment` header and saves the file, whereas
 * `window.open` just opens the CSV in a new tab (the browser renders it as text).
 */
function triggerDownload(url: string, filename?: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? "";
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export type ExportStatus = "idle" | "running" | "done" | "error";

type JobPoll = {
  status?: string;
  error?: string | null;
  progress?: number;
};

/**
 * Drives the workbook CSV export — the app's primary action. Exposes live
 * `status`/`progress` so the top-bar CTA can render "Exporting… 45%" inline and
 * settle to "Downloaded" / surface an error. Polls the job every 1500ms and
 * saves the CSV file when done. Behaviour of the export itself is unchanged.
 */
export function useExportJob(workbookId: string): {
  exportWorkbook: () => Promise<void>;
  status: ExportStatus;
  progress: number;
  error: string | null;
} {
  const [status, setStatus] = useState<ExportStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Guard against double-clicks kicking off concurrent exports.
  const runningRef = useRef(false);

  const exportWorkbook = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setStatus("running");
    setProgress(0);
    setError(null);
    try {
      const json = await createWorkbookExport(workbookId, {});
      if (!json?.jobId) {
        throw new Error("Export did not return a job id.");
      }
      const jobId = json.jobId;
      for (;;) {
        await wait(1500);
        const job = (await getExportJob(jobId)) as JobPoll;
        if (typeof job.progress === "number") {
          setProgress(Math.max(0, Math.min(100, Math.round(job.progress))));
        }
        if (job.status === "done") {
          setProgress(100);
          break;
        }
        if (job.status === "error") throw new Error(job.error ?? "Export failed.");
        if (job.status === "cancelled") throw new Error("Export cancelled.");
      }
      setStatus("done");
      const blob = await downloadExportJobBlob(jobId);
      const url = URL.createObjectURL(blob);
      triggerDownload(url, `export-${jobId}.csv`);
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
      // Settle the button back to idle after the user has seen "Downloaded".
      window.setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Request failed (${err.status}): ${err.message}`
          : err instanceof Error
            ? err.message
            : String(err);
      setError(message);
      setStatus("error");
      window.alert(`Export failed: ${message}`);
      window.setTimeout(() => setStatus("idle"), 5000);
    } finally {
      runningRef.current = false;
    }
  }, [workbookId]);

  return { exportWorkbook, status, progress, error };
}
